import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Handshake, Lock } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import {
  BrutalSelect,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectTrigger,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import { SEO } from "@/components/SEO";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Partnership {
  id: string;
  name: string;
  image_url: string | null;
  category: string | null;
}

const CATEGORY_KEYS = ["all", "software", "formazione", "servizi", "prodotti", "altro"];

export default function Partners() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isSubscriber } = useAuth();
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");

  const hasAccess = user && isSubscriber;

  useEffect(() => {
    if (!user) {
      navigate("/auth/login");
      return;
    }
    if (!isSubscriber) {
      return;
    }
    fetchPartnerships();
  }, [user, isSubscriber]);

  const fetchPartnerships = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("partnerships")
      .select("id, name, image_url, category")
      .eq("is_active", true)
      .order("display_order", { ascending: true });

    if (!error && data) {
      setPartnerships(data);
    }
    setLoading(false);
  };

  const filteredPartnerships = partnerships.filter((p) => {
    return categoryFilter === "all" || p.category === categoryFilter;
  });

  // No access view
  if (!hasAccess) {
    return (
      <>
        <SEO
          title={t("partners.seoTitle")}
          description={t("partners.seoDescription")}
        />
        <div className="flex flex-col min-h-screen">
          <Header />
          <main className="flex-1 bg-surface py-16">
            <div className="max-w-2xl mx-auto px-4 text-center">
              <SEOBreadcrumb items={[{ label: t('partners.breadcrumb') }]} />
              <BrutalCard className="p-12">
                <Lock className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
                <h1 className="font-heading text-3xl font-bold mb-4">
                  {t("partners.accessDenied")}
                </h1>
                <p className="text-muted-foreground mb-6">
                  {t("partners.subscriptionRequired")}
                </p>
                <BrutalButton onClick={() => navigate("/pricing")}>
                  {t("partners.viewPlans")}
                </BrutalButton>
              </BrutalCard>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={t("partners.seoTitle")}
        description={t("partners.seoDescription")}
      />
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-1 bg-surface py-12">
          <div className="max-w-7xl mx-auto px-4">
            <SEOBreadcrumb items={[{ label: t('partners.breadcrumb') }]} />
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="font-heading font-bold text-4xl md:text-6xl text-dark mb-4">
                {t("partners.title")}
              </h1>
              <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
                {t("partners.subtitle")}
              </p>
            </div>

            {/* Filter */}
            <div className="flex justify-center mb-8">
              <div className="w-full max-w-xs">
                <BrutalSelect value={categoryFilter} onValueChange={setCategoryFilter}>
                  <BrutalSelectTrigger>
                    <BrutalSelectValue />
                  </BrutalSelectTrigger>
                  <BrutalSelectContent>
                    {CATEGORY_KEYS.map((key) => (
                      <BrutalSelectItem key={key} value={key}>
                        {t(`partners.categories.${key}`)}
                      </BrutalSelectItem>
                    ))}
                  </BrutalSelectContent>
                </BrutalSelect>
              </div>
            </div>

            {/* Portfolio Grid */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">{t("common.loading")}</p>
              </div>
            ) : filteredPartnerships.length === 0 ? (
              <BrutalCard className="p-12 text-center max-w-xl mx-auto">
                <Handshake className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {t("partners.empty")}
                </p>
              </BrutalCard>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPartnerships.map((partnership) => (
                  <div
                    key={partnership.id}
                    onClick={() => navigate(`/partners/${partnership.id}`)}
                    className="cursor-pointer group relative aspect-[5/4] overflow-hidden border-[3px] border-dark shadow-brutal bg-muted"
                  >
                    {/* Image */}
                    {partnership.image_url ? (
                      <img
                        src={partnership.image_url}
                        alt={partnership.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <span className="text-6xl">🤝</span>
                      </div>
                    )}

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-dark/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center p-4">
                      <h3 className="font-heading font-bold text-xl md:text-2xl text-card text-center">
                        {partnership.name}
                      </h3>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
